import axios from "axios";
import eventBus from "./eventBus";
import { useAuthStore } from '../store/store';

// Methods that represent a state change — these auto-fire a toast when the
// API returns a `message` but no explicit `toast` field.
const WRITE_METHODS = ['post', 'put', 'patch', 'delete'];

/**
 * Derive the most appropriate toast variant from the HTTP status code
 * and the response body's `status` boolean.
 *
 *   success  — 2xx + status:true
 *   warning  — 2xx + status:false  |  400–422 client/validation errors
 *   info     — 404 not found
 *   danger   — 401 / 403 auth errors  |  5xx server errors
 */
const inferType = (httpStatus, bodyStatus) => {
    if (httpStatus >= 500)                          return 'danger';
    if (httpStatus === 401 || httpStatus === 403)   return 'danger';
    if (httpStatus === 404)                         return 'info';
    if (httpStatus >= 400)                          return 'warning';
    // 2xx
    if (bodyStatus === false)                       return 'warning';
    return 'success';
};

const axiosInstance = axios.create({
    baseURL: process.env.REACT_APP_API_URL + '/api/',
});

// ── Response interceptor ────────────────────────────────────────────────────
axiosInstance.interceptors.response.use(
    res => {
        const { toast, message, status: bodyStatus } = res.data || {};

        if (toast?.message) {
            // Explicit toast from the API — honour it verbatim
            eventBus.emit('toast', {
                type:    toast.type || 'success',
                message: toast.message,
            });
        } else if (message && WRITE_METHODS.includes(res.config?.method?.toLowerCase())) {
            // Write operation returned a message with no explicit toast — auto-fire
            // eventBus.emit('toast', {
            //     type:    inferType(res.status, bodyStatus),
            //     message,
            // });
        }

        return res;
    },

    err => {
        if (!err.response) return Promise.reject(err);

        const { status }  = err.response;
        const bodyMessage = err.response.data?.message;

        if (status === 401) {
            eventBus.emit('toast',        { type: 'danger',  message: bodyMessage || 'Unauthorised' });
            eventBus.emit('unauthorized');
        } else if (status === 403) {
            eventBus.emit('toast',        { type: 'danger',  message: bodyMessage || 'Forbidden' });
        } else if (status === 404) {
            eventBus.emit('toast',        { type: 'info',    message: bodyMessage || 'Not found' });
        } else if (status === 422) {
            eventBus.emit('toast',        { type: 'warning', message: bodyMessage || 'Validation error' });
        } else if (status >= 400 && status < 500) {
            eventBus.emit('toast',        { type: 'warning', message: bodyMessage || 'Client error' });
        } else {
            eventBus.emit('toast',        { type: 'danger',  message: bodyMessage || 'Unexpected server error' });
        }

        return Promise.reject(err);
    }
);

// ── Request interceptor — attach Bearer token ───────────────────────────────
axiosInstance.interceptors.request.use(
    config => {
        const token = useAuthStore.getState().token;
        if (token) config.headers.Authorization = `Bearer ${token}`;
        return config;
    },
    error => Promise.reject(error)
);

export default {
    get:    axiosInstance.get,
    post:   axiosInstance.post,
    put:    axiosInstance.put,
    patch:  axiosInstance.patch,
    delete: axiosInstance.delete,
};
